import moment from "moment";
import { sale as saleType } from "../../typings/sale";
import { Events, HttpStatusCode } from "../../thor.enums";
import { getSalesUrl } from "../../utils/routes";
import { TranslationsStatus } from "../../typings/translations";
import { auth } from "../../typings/auth";
import { StripeContractStatus } from "../../typings/payment";

export class SaleEditController {
    public static $inject: string[] = [
        "$mdDialog",
        "$mdMedia",
        "$rootScope",
        "$scope",
        "$state",
        "$translate",
        "$window",
        "multipleSelect",
        "sale",
        "toaster",
        "authService",
        "saleService",
        "sitesService",
        "utilsService",
        "absenteeBidsService",
        "contractService"
    ];

    public selectedTabName;
    public isOrgaClientInfosRestricted: boolean;
    public isStatsActivityRestricted: boolean;
    public absenteeBid: saleType.AbsenteeBidData;
    public itemsCount: number;
    public supportPhone: string;
    public isStatsTabDisplayable: boolean;
    public isPaymentsTabDisplayable: boolean = false;
    public isNewBadgeDisplayed: boolean = false;

    private $t;

    constructor(
        private $mdDialog: ng.material.IDialogService,
        private $mdMedia: ng.material.IMedia,
        private $rootScope: ng.IRootScopeService,
        $scope: ng.IScope,
        private $state: ng.ui.IStateService,
        $translate: ng.translate.ITranslateService,
        private $window: ng.IWindowService,
        public multipleSelect,
        private sale,
        private toaster,
        private authService,
        private saleService,
        public sitesService,
        private utilsService,
        public absenteeBidsService,
        public contractService
    ) {
        this.$t = $translate.instant;

        $rootScope.$on(Events.ItemUpdated, () => this.setItemsCount());

        $scope.isNaN = (v: any) => isNaN(v);

        $scope.$watch(
            () => this.sale.itemsLoading,
            () => this.setItemsCount()
        );
    }

    public async $onInit() {
        this.setItemsCount();
        this.absenteeBidsService.getAbsenteeBidsCountForSales([this.sale.id]);
        this.$rootScope.$watchCollection(
            () => {
                return this.absenteeBidsService.saleAbsenteeBidsSuccess;
            },
            saleAbsenteeBidsSuccess => {
                if (saleAbsenteeBidsSuccess.length) {
                    this.absenteeBid = this.absenteeBidsService.getAbsenteeBidsCountForSale(this.sale.id);
                }
            }
        );
        this.selectedTabName = this.$state.current.name.split(".").splice(-1).pop();
        const userRights = this.authService.getUserRights();
        this.isOrgaClientInfosRestricted = !userRights[auth.Scopes.ORGANIZATION_CLIENT_INFORMATIONS];
        this.isStatsActivityRestricted = !userRights[auth.Scopes.STATS_ACTIVITY];
        this.supportPhone = this.authService.isInternational()
            ? this.sitesService.contactPhoneInter
            : this.sitesService.contactPhone;
        this.isStatsTabDisplayable =
            this.sale.resource.sites.interencheres.type === "live" &&
            this.sale.resource.first_published &&
            moment().diff(moment(this.sale.resource.first_published), "minutes") > 15 &&
                (
                    this.sale.resource.status === "published" ||
                    this.sale.resource.status === "edited"
                );


        if (this.sale.resource.sites.interencheres.type === "live" || this.sale.resource.sites.auction.type === "live") {
           const contracts = this.contractService.contracts;

            const hasMatchingContract = contracts?.some(
                (contract) => {
                    return contract.provider === this.sale.resource.payment_provider && contract.status === StripeContractStatus.Activated;
                }
            );

            this.isPaymentsTabDisplayable = (this.sale.resource.isPast || this.sale.resource.startedToday) && hasMatchingContract;
        }
    }

    public deleteMediaResults() {
        this.sale.deleteMediaResults()
            .then(() => {
                this.toaster.show(this.$t("fichier_supprime_avec_succes"));
            })
            .catch(() => {
                this.toaster.show(this.$t("une_erreur_est_survenue"));
            });
    }

    public handleBackButton() {
        this.sale.saleService.cancelItemsQueriesIfRunning();
        this.$window.location.href = getSalesUrl(this.sale.resource?.isPast);
    }

    public setMediaResults(event, file) {
        if (!file || !file.name) {
            return;
        }

        if (this.utilsService.getFileExtension(file.name) !== "pdf") {
            this.toaster.show(this.$t("seuls_les_fichiers_de_type_n_sont_autorises", ["PDF"]));
            return;
        }

        this.$mdDialog.show({
            template: require("../../core/upload/media.html"),
            parent: angular.element(document.body),
            targetEvent: event,
            clickOutsideToClose: false,
            fullscreen: this.$mdMedia("xs"),
            onComplete: () => {
                this.sale.setMediaResults(file)
                    .then(() => {
                        this.toaster.show(this.$t("fichier_ajoute_avec_succes"));
                    })
                    .catch(error => {
                        const errorMessage = error.status === 413
                            ? this.$t(
                                "fichier_trop_volumineux_la_taille_ne_doit_pas_exceder_n_mo",
                                { N: 10 }
                            )
                            : this.$t("une_erreur_est_survenue");
                        this.toaster.show(errorMessage, true);
                    })
                    .finally(() => {
                        this.$mdDialog.hide();
                    });
            }
        });
    }

    public async initiatePublish(
        { checkTranslationStatus, event }: { checkTranslationStatus?: boolean; event?: PointerEvent } = {
            checkTranslationStatus: true
        }
    ) {
        if (!this.sale.isPublishable) {
            this.toaster.show(this.$t("il_n_y_a_rien_a_publier"));
            return;
        }

        if (checkTranslationStatus) {
            await this.sale.getSaleTranslationStatus();

            if (this.sale.saleTranslationsStatus.status !== TranslationsStatus.Done) {
                this.toaster.show(this.$t("translations.toaster.sale.error"), true);
                return;
            }
        }

        this.sale.checkIfEveryMandatoryFieldsAreFilled();

        if (this.sale.isEveryMandatoryFieldsFilled) {
            // Here we need to get fresh meta live data (ex: hasStarted)
            this.sale.setOrFetchLiveResource()
                .finally(() => {
                    this.$mdDialog.show({
                        controller: "SummaryController",
                        controllerAs: "summaryCtrl",
                        template: require("./summary-sale.html"),
                        locals: {
                            sale: this.sale
                        },
                        bindToController: true,
                        parent: angular.element(document.body),
                        targetEvent: event,
                        clickOutsideToClose: false,
                        fullscreen: this.$mdMedia("xs")
                    });
                });
        } else {
            this.$mdDialog.show({
                controller: "MissingFieldController",
                controllerAs: "missingFieldCtrl",
                template: require("./missing-field.html"),
                locals: {
                    sale: this.sale
                },
                bindToController: true,
                parent: angular.element(document.body),
                targetEvent: event,
                clickOutsideToClose: true,
                fullscreen: this.$mdMedia("xs")
            });
        }
    }

    public reset(event) {
        const broadcast = () => this.$rootScope.$broadcast(Events.PostMessageToV2);
        const confirm = this.$mdDialog
            .confirm()
            .title(this.$t("confirmation_de_l_annulation_des_modifications"))
            .textContent(this.$t("je_souhaite_annuler_toutes_les_modifications_apportees"))
            .ariaLabel(this.$t("confirmation_de_l_annulation_des_modifications"))
            .targetEvent(event)
            .ok(this.$t("confirmer"))
            .cancel(this.$t("annuler"));

        this.$mdDialog.show(confirm).then(() =>
            this.sale
                .reset()
                .then(() => broadcast())
                .catch(error => {
                    if (error.status === HttpStatusCode.NotModified) {
                        broadcast();
                        return;
                    }
                    this.toaster.show(this.$t("une_erreur_est_survenue"), true);
                })
        );
    }

    private async setItemsCount() {
        const { count } = await this.saleService.getItemsCount(this.sale.id);
        this.itemsCount = count || 0;
    }
}
